import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./Styles.css";
import HeaderToMainPage from "../../Components/Universal/Header/Index";
import Footer from "../../Components/Universal/Footer/Index";

const ETHOSINO_TERMS_EFFECTIVE = "September 8, 2026";
const ETHOSINO_TERMS_UPDATED = "September 8, 2026";

const ethosinoTermsSections = [
    {
        id: "ethosino-terms-section-agreement",
        number: "01",
        label: "Agreement to Terms",
    },
    {
        id: "ethosino-terms-section-platform",
        number: "02",
        label: "The Ethosino Platform",
    },
    {
        id: "ethosino-terms-section-eligibility",
        number: "03",
        label: "Eligibility",
    },
    {
        id: "ethosino-terms-section-accounts",
        number: "04",
        label: "Accounts & Security",
    },
    {
        id: "ethosino-terms-section-operator",
        number: "05",
        label: "Operator Environments",
    },
    {
        id: "ethosino-terms-section-gaming",
        number: "06",
        label: "Gaming & Participation",
    },
    {
        id: "ethosino-terms-section-wallets",
        number: "07",
        label: "Wallets & Transactions",
    },
    {
        id: "ethosino-terms-section-bonuses",
        number: "08",
        label: "Bonuses & Promotions",
    },
    {
        id: "ethosino-terms-section-fairness",
        number: "09",
        label: "Game Integrity & Fairness",
    },
    {
        id: "ethosino-terms-section-prohibited",
        number: "10",
        label: "Prohibited Conduct",
    },
    {
        id: "ethosino-terms-section-compliance",
        number: "11",
        label: "Compliance & Verification",
    },
    {
        id: "ethosino-terms-section-third-party",
        number: "12",
        label: "Third-Party Services",
    },
    {
        id: "ethosino-terms-section-intellectual-property",
        number: "13",
        label: "Intellectual Property",
    },
    {
        id: "ethosino-terms-section-availability",
        number: "14",
        label: "Availability & Changes",
    },
    {
        id: "ethosino-terms-section-disclaimers",
        number: "15",
        label: "Disclaimers",
    },
    {
        id: "ethosino-terms-section-liability",
        number: "16",
        label: "Limitation of Liability",
    },
    {
        id: "ethosino-terms-section-indemnity",
        number: "17",
        label: "Indemnification",
    },
    {
        id: "ethosino-terms-section-termination",
        number: "18",
        label: "Suspension & Termination",
    },
    {
        id: "ethosino-terms-section-disputes",
        number: "19",
        label: "Disputes & Governing Law",
    },
    {
        id: "ethosino-terms-section-changes",
        number: "20",
        label: "Changes to These Terms",
    },
    {
        id: "ethosino-terms-section-contact",
        number: "21",
        label: "Contact",
    },
];

function EthosinoTermsIcon({ type = "shield" }) {
    const iconPaths = {
        shield: (
            <>
                <path d="M12 3 5 6v5c0 4.7 2.8 8.9 7 10 4.2-1.1 7-5.3 7-10V6l-7-3Z" />
                <path d="m9.4 12 1.7 1.7 3.8-4" />
            </>
        ),

        lock: (
            <>
                <rect x="5" y="10" width="14" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </>
        ),

        wallet: (
            <>
                <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4H18v16H6.5A2.5 2.5 0 0 1 4 17.5Z" />
                <path d="M15 10h5v5h-5a2.5 2.5 0 0 1 0-5Z" />
            </>
        ),

        user: (
            <>
                <circle cx="12" cy="8" r="4" />
                <path d="M5 21a7 7 0 0 1 14 0" />
            </>
        ),

        warning: (
            <>
                <path d="M12 3 2.8 20h18.4Z" />
                <path d="M12 9v5" />
                <path d="M12 17h.01" />
            </>
        ),

        game: (
            <>
                <rect x="3" y="7" width="18" height="11" rx="4" />
                <path d="M8 10v5" />
                <path d="M5.5 12.5h5" />
                <circle cx="16" cy="11.5" r=".8" />
                <circle cx="18.5" cy="14" r=".8" />
            </>
        ),

        scale: (
            <>
                <path d="M12 3v18" />
                <path d="M5 6h14" />
                <path d="m5 6-3 6h6Z" />
                <path d="m19 6-3 6h6Z" />
                <path d="M7 21h10" />
            </>
        ),

        code: (
            <>
                <path d="m8 9-4 3 4 3" />
                <path d="m16 9 4 3-4 3" />
                <path d="m14 5-4 14" />
            </>
        ),
    };

    return (
        <svg
            className="ethosino-terms-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {iconPaths[type]}
        </svg>
    );
}

function EthosinoTermsSection({
    id,
    number,
    eyebrow,
    title,
    lead,
    theme = "cyan",
    children,
}) {
    return (
        <section
            id={id}
            className={`ethosino-terms-section ethosino-terms-section--${theme} ethosino-terms-reveal`}
        >
            <div className="ethosino-terms-section-number" aria-hidden="true">
                {number}
            </div>

            <div className="ethosino-terms-section-content">
                <div className="ethosino-terms-section-eyebrow">{eyebrow}</div>

                <h2>{title}</h2>

                {lead && <p className="ethosino-terms-section-lead">{lead}</p>}

                {children}
            </div>
        </section>
    );
}

function EthosinoTermsList({ children }) {
    return <ul className="ethosino-terms-list">{children}</ul>;
}

function EthosinoTermsCard({
    title,
    icon = "shield",
    children,
    status,
}) {
    return (
        <article className="ethosino-terms-card">
            <div className="ethosino-terms-card-icon">
                <EthosinoTermsIcon type={icon} />
            </div>

            <div className="ethosino-terms-card-copy">
                {status && (
                    <span className="ethosino-terms-card-status">{status}</span>
                )}

                <h3>{title}</h3>
                <p>{children}</p>
            </div>
        </article>
    );
}

export default function TermsConditionsPage() {
    const [ethosinoTermsActiveSection, setEthosinoTermsActiveSection] =
        useState("ethosino-terms-section-agreement");

    const [ethosinoTermsProgress, setEthosinoTermsProgress] = useState(0);

    const ethosinoTermsSectionIds = useMemo(
        () => ethosinoTermsSections.map((section) => section.id),
        []
    );

    useEffect(() => {
        const handleEthosinoTermsScroll = () => {
            const documentElement = document.documentElement;

            const maxScroll =
                documentElement.scrollHeight - window.innerHeight;

            const currentProgress =
                maxScroll > 0
                    ? Math.min((window.scrollY / maxScroll) * 100, 100)
                    : 0;

            setEthosinoTermsProgress(currentProgress);

            let currentSection = ethosinoTermsSectionIds[0];

            ethosinoTermsSectionIds.forEach((sectionId) => {
                const sectionElement = document.getElementById(sectionId);

                if (!sectionElement) return;

                const sectionRect = sectionElement.getBoundingClientRect();

                if (sectionRect.top <= 190) {
                    currentSection = sectionId;
                }
            });

            setEthosinoTermsActiveSection(currentSection);
        };

        const revealObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add(
                            "ethosino-terms-reveal--visible"
                        );
                    }
                });
            },
            {
                threshold: 0.07,
                rootMargin: "0px 0px -40px 0px",
            }
        );

        document
            .querySelectorAll(".ethosino-terms-reveal")
            .forEach((element) => revealObserver.observe(element));

        window.addEventListener("scroll", handleEthosinoTermsScroll, {
            passive: true,
        });

        handleEthosinoTermsScroll();

        return () => {
            window.removeEventListener(
                "scroll",
                handleEthosinoTermsScroll
            );

            revealObserver.disconnect();
        };
    }, [ethosinoTermsSectionIds]);

    const scrollEthosinoTermsToSection = (sectionId) => {
        document.getElementById(sectionId)?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <Fragment>
            <HeaderToMainPage />
            <main
                id="ethosino-terms-page"
                className="ethosino-terms-page"
            >
                <div
                    id="ethosino-terms-progress"
                    className="ethosino-terms-progress"
                    style={{ width: `${ethosinoTermsProgress}%` }}
                />

                {/* BACKGROUND */}
                <div
                    id="ethosino-terms-background"
                    className="ethosino-terms-background"
                    aria-hidden="true"
                >
                    <div className="ethosino-terms-background-grid" />

                    <div className="ethosino-terms-background-line ethosino-terms-background-line--one" />
                    <div className="ethosino-terms-background-line ethosino-terms-background-line--two" />

                    <div className="ethosino-terms-background-orb ethosino-terms-background-orb--cyan" />
                    <div className="ethosino-terms-background-orb ethosino-terms-background-orb--purple" />
                    <div className="ethosino-terms-background-orb ethosino-terms-background-orb--blue" />
                </div>

                {/* =====================================================
          HERO
      ====================================================== */}
                <header
                    id="ethosino-terms-hero"
                    className="ethosino-terms-hero"
                >
                    <div className="ethosino-terms-shell">
                        <div className="ethosino-terms-topbar">
                            <a
                                href="/"
                                className="ethosino-terms-brand"
                                aria-label="Ethosino home"
                            >
                                <span className="ethosino-terms-brand-mark">
                                    <span />
                                    <span />
                                </span>

                                <span className="ethosino-terms-brand-name">
                                    ETHOSINO
                                </span>
                            </a>

                            <div className="ethosino-terms-topbar-links">
                                <a href="/privacy">Privacy</a>

                                <a
                                    href="/"
                                    className="ethosino-terms-return-link"
                                >
                                    Return to Ethosino
                                    <span>↗</span>
                                </a>
                            </div>
                        </div>

                        <div className="ethosino-terms-hero-grid">
                            <div className="ethosino-terms-hero-copy">
                                <div className="ethosino-terms-kicker">
                                    <span className="ethosino-terms-kicker-pulse" />

                                    PLATFORM / LEGAL
                                </div>

                                <h1>
                                    Clear rules.
                                    <span> Serious infrastructure.</span>
                                </h1>

                                <p className="ethosino-terms-hero-intro">
                                    These Terms and Conditions govern access to and use
                                    of Ethosino websites, software, gaming
                                    infrastructure, operator environments, player
                                    experiences, APIs, administrative tools, and related
                                    services.
                                </p>

                                <div className="ethosino-terms-hero-meta">
                                    <div>
                                        <span>EFFECTIVE</span>
                                        <strong>{ETHOSINO_TERMS_EFFECTIVE}</strong>
                                    </div>

                                    <div>
                                        <span>LAST UPDATED</span>
                                        <strong>{ETHOSINO_TERMS_UPDATED}</strong>
                                    </div>

                                    <div>
                                        <span>DOCUMENT</span>
                                        <strong>Terms v1.0</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="ethosino-terms-hero-console ethosino-terms-reveal">
                                <div className="ethosino-terms-console-window">
                                    <div className="ethosino-terms-console-header">
                                        <div className="ethosino-terms-console-dots">
                                            <i />
                                            <i />
                                            <i />
                                        </div>

                                        <span>platform.rules</span>

                                        <div className="ethosino-terms-console-state">
                                            <i />
                                            ENFORCED
                                        </div>
                                    </div>

                                    <div className="ethosino-terms-console-body">
                                        <div className="ethosino-terms-console-command">
                                            <span>01</span>
                                            <strong>operator_scope</strong>
                                            <i>=</i>
                                            <em>"defined"</em>
                                        </div>

                                        <div className="ethosino-terms-console-command">
                                            <span>02</span>
                                            <strong>fair_play</strong>
                                            <i>=</i>
                                            <em>"required"</em>
                                        </div>

                                        <div className="ethosino-terms-console-command">
                                            <span>03</span>
                                            <strong>fraud</strong>
                                            <i>=</i>
                                            <em className="ethosino-terms-console-red">
                                                false
                                            </em>
                                        </div>

                                        <div className="ethosino-terms-console-command">
                                            <span>04</span>
                                            <strong>account_security</strong>
                                            <i>=</i>
                                            <em>"enforced"</em>
                                        </div>

                                        <div className="ethosino-terms-console-command">
                                            <span>05</span>
                                            <strong>jurisdiction</strong>
                                            <i>=</i>
                                            <em>"verified"</em>
                                        </div>

                                        <div className="ethosino-terms-console-divider" />

                                        <div className="ethosino-terms-console-summary">
                                            <span>
                                                21 POLICY LAYERS
                                            </span>

                                            <strong>
                                                PLATFORM READY
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="ethosino-terms-floating-panel ethosino-terms-floating-panel--one">
                                    <span>
                                        <EthosinoTermsIcon type="scale" />
                                    </span>

                                    <div>
                                        <small>Terms framework</small>
                                        <strong>Operator-aware</strong>
                                    </div>
                                </div>

                                <div className="ethosino-terms-floating-panel ethosino-terms-floating-panel--two">
                                    <span>
                                        <EthosinoTermsIcon type="shield" />
                                    </span>

                                    <div>
                                        <small>Platform integrity</small>
                                        <strong>Protected by design</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="ethosino-terms-hero-principle ethosino-terms-reveal">
                            <div className="ethosino-terms-hero-principle-label">
                                <span>THE OPERATING PRINCIPLE</span>

                                <strong>
                                    Access carries responsibility.
                                </strong>
                            </div>

                            <p>
                                Ethosino provides sophisticated gaming and operator
                                infrastructure. Users, operators, partners, and
                                administrators are responsible for using that
                                infrastructure lawfully, honestly, securely, and in
                                accordance with the applicable rules of their
                                jurisdiction.
                            </p>
                        </div>
                    </div>
                </header>

                {/* =====================================================
          DOCUMENT
      ====================================================== */}
                <div className="ethosino-terms-shell ethosino-terms-document-layout">
                    <aside className="ethosino-terms-navigation">
                        <div className="ethosino-terms-navigation-sticky">
                            <span className="ethosino-terms-navigation-title">
                                TERMS INDEX
                            </span>

                            <nav aria-label="Terms and Conditions">
                                {ethosinoTermsSections.map((section) => (
                                    <button
                                        key={section.id}
                                        type="button"
                                        onClick={() =>
                                            scrollEthosinoTermsToSection(section.id)
                                        }
                                        className={
                                            ethosinoTermsActiveSection === section.id
                                                ? "ethosino-terms-navigation-item ethosino-terms-navigation-item--active"
                                                : "ethosino-terms-navigation-item"
                                        }
                                    >
                                        <span>{section.number}</span>
                                        {section.label}
                                    </button>
                                ))}
                            </nav>

                            <div className="ethosino-terms-navigation-legal">
                                <span>Legal inquiry</span>

                                <a href="mailto:legal@ethosino.com">
                                    legal@ethosino.com
                                </a>
                            </div>
                        </div>
                    </aside>

                    <article className="ethosino-terms-document">
                        {/* 01 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-agreement"
                            number="01"
                            eyebrow="YOUR AGREEMENT"
                            title="Agreement to these Terms"
                            lead="By accessing or using a service governed by these Terms, you agree to comply with this agreement and all rules applicable to the particular service you use."
                            theme="cyan"
                        >
                            <p>
                                These Terms and Conditions ("Terms") constitute a
                                legally binding agreement between you and the
                                applicable Ethosino legal entity concerning your access
                                to and use of Ethosino websites, software,
                                applications, operator infrastructure, administrative
                                interfaces, game systems, APIs, communications, and
                                related services collectively referred to as the
                                "Services."
                            </p>

                            <p>
                                If you do not agree to these Terms, you should not
                                access or use the Services.
                            </p>

                            <p>
                                Additional terms may apply to particular products,
                                promotions, operator environments, games, payment
                                methods, jurisdictions, tournaments, APIs, or services.
                                When additional terms apply, those terms become part of
                                your agreement governing the relevant activity.
                            </p>

                            <div className="ethosino-terms-highlight">
                                <div className="ethosino-terms-highlight-icon">
                                    <EthosinoTermsIcon type="scale" />
                                </div>

                                <div>
                                    <span>IMPORTANT</span>

                                    <p>
                                        If you use Ethosino through an independently
                                        operated gaming property, you may also be bound by
                                        that operator's own terms, rules, privacy policy,
                                        eligibility requirements, and promotional
                                        conditions.
                                    </p>
                                </div>
                            </div>
                        </EthosinoTermsSection>

                        {/* 02 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-platform"
                            number="02"
                            eyebrow="THE TECHNOLOGY"
                            title="The Ethosino platform"
                            theme="purple"
                        >
                            <p>
                                Ethosino provides technology designed to support
                                digital gaming, promotional gaming, skill gaming,
                                tournament systems, player account management, wallet
                                infrastructure, operator administration, content
                                integrations, compliance systems, analytics, and
                                related functionality.
                            </p>

                            <p>
                                Depending on the particular deployment, Ethosino may
                                provide technology directly, provide technology to an
                                independent operator, provide demonstration software,
                                integrate third-party products, or perform a
                                combination of these activities.
                            </p>

                            <div className="ethosino-terms-card-grid">
                                <EthosinoTermsCard
                                    title="Platform layer"
                                    icon="code"
                                    status="CORE"
                                >
                                    Ethosino may provide software, APIs, operator
                                    dashboards, user interfaces, wallets, game systems,
                                    account systems, analytics, and infrastructure.
                                </EthosinoTermsCard>

                                <EthosinoTermsCard
                                    title="Operator layer"
                                    icon="user"
                                    status="TENANT"
                                >
                                    Independent operators may configure branding,
                                    promotions, games, commercial rules, eligibility, and
                                    other elements within their environment.
                                </EthosinoTermsCard>

                                <EthosinoTermsCard
                                    title="Provider layer"
                                    icon="game"
                                    status="CONNECTED"
                                >
                                    Third-party game studios, payment providers,
                                    verification systems, communications providers, and
                                    other vendors may connect to the platform.
                                </EthosinoTermsCard>

                                <EthosinoTermsCard
                                    title="Player layer"
                                    icon="wallet"
                                    status="USER"
                                >
                                    Users may access games, accounts, wallets,
                                    promotions, tournaments, support tools, or other
                                    services made available by the applicable operator.
                                </EthosinoTermsCard>
                            </div>

                            <p>
                                The precise functionality available to you may differ
                                between Ethosino deployments and may change over time.
                            </p>
                        </EthosinoTermsSection>

                        {/* 03 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-eligibility"
                            number="03"
                            eyebrow="ACCESS REQUIREMENTS"
                            title="Eligibility"
                            theme="green"
                        >
                            <p>
                                You may use the Services only if you are legally
                                permitted to do so under applicable law and satisfy all
                                eligibility requirements imposed by the relevant
                                service or operator.
                            </p>

                            <EthosinoTermsList>
                                <li>
                                    You must satisfy any applicable minimum age
                                    requirement.
                                </li>

                                <li>
                                    You must have the legal capacity to enter into a
                                    binding agreement.
                                </li>

                                <li>
                                    You must use the Services only from jurisdictions in
                                    which the applicable activity is lawful and
                                    permitted.
                                </li>

                                <li>
                                    You must not access a restricted service through a
                                    VPN, proxy, location-spoofing tool, false identity,
                                    or other method designed to bypass eligibility
                                    controls.
                                </li>

                                <li>
                                    You must provide truthful and accurate information
                                    when identity, age, location, or eligibility
                                    information is requested.
                                </li>

                                <li>
                                    You may be required to complete identity,
                                    verification, compliance, or risk-review procedures
                                    before accessing certain functionality.
                                </li>
                            </EthosinoTermsList>

                            <div className="ethosino-terms-age-panel">
                                <div>
                                    <span>AGE + LOCATION</span>

                                    <h3>
                                        Eligibility is determined by the applicable
                                        deployment.
                                    </h3>
                                </div>

                                <p>
                                    Gaming laws and eligibility rules vary substantially
                                    by jurisdiction. The applicable operator may impose
                                    restrictions beyond the minimum requirements imposed
                                    by law.
                                </p>
                            </div>
                        </EthosinoTermsSection>

                        {/* 04 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-accounts"
                            number="04"
                            eyebrow="IDENTITY + ACCESS"
                            title="Accounts and account security"
                            theme="cyan"
                        >
                            <p>
                                Certain Services require an account. You are
                                responsible for maintaining accurate account
                                information and protecting the credentials used to
                                access your account.
                            </p>

                            <p>You agree to:</p>

                            <EthosinoTermsList>
                                <li>
                                    provide accurate, current, and complete registration
                                    information;
                                </li>

                                <li>
                                    maintain only accounts that you are authorized to
                                    use;
                                </li>

                                <li>
                                    keep passwords, access codes, security credentials,
                                    and authentication devices confidential;
                                </li>

                                <li>
                                    promptly notify the applicable operator or Ethosino
                                    of suspected unauthorized access;
                                </li>

                                <li>
                                    refrain from selling, transferring, lending, or
                                    sharing an account where prohibited; and
                                </li>

                                <li>
                                    accept responsibility for activity occurring through
                                    your account to the extent permitted by law.
                                </li>
                            </EthosinoTermsList>

                            <p>
                                Ethosino or the applicable operator may require
                                additional authentication, temporarily lock an account,
                                reset credentials, restrict withdrawals, or take other
                                reasonable security measures where suspicious activity
                                is detected.
                            </p>
                        </EthosinoTermsSection>

                        {/* 05 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-operator"
                            number="05"
                            eyebrow="MULTI-TENANT INFRASTRUCTURE"
                            title="Independent operator environments"
                            theme="purple"
                        >
                            <p>
                                Ethosino is designed as multi-tenant infrastructure.
                                Individual businesses may operate independently branded
                                environments using Ethosino technology.
                            </p>

                            <p>
                                Unless expressly stated otherwise, an independent
                                operator may be responsible for matters including:
                            </p>

                            <EthosinoTermsList>
                                <li>operator licensing and regulatory status;</li>
                                <li>player acquisition and marketing;</li>
                                <li>promotional rules and bonus terms;</li>
                                <li>customer support decisions;</li>
                                <li>withdrawal approval policies;</li>
                                <li>commercial pricing and promotional offers;</li>
                                <li>game availability;</li>
                                <li>jurisdictional access restrictions;</li>
                                <li>responsible-gaming controls;</li>
                                <li>tax reporting where applicable;</li>
                                <li>player eligibility; and</li>
                                <li>
                                    compliance obligations assigned to that operator by
                                    law.
                                </li>
                            </EthosinoTermsList>

                            <div className="ethosino-terms-operator-panel">
                                <div className="ethosino-terms-operator-panel-top">
                                    <span>SHARED CORE</span>
                                    <span>DISTINCT ENVIRONMENTS</span>
                                </div>

                                <div className="ethosino-terms-operator-diagram">
                                    <div>
                                        <strong>ETHOSINO</strong>
                                        <small>Platform infrastructure</small>
                                    </div>

                                    <span>→</span>

                                    <div>
                                        <strong>OPERATOR</strong>
                                        <small>Brand + commercial rules</small>
                                    </div>

                                    <span>→</span>

                                    <div>
                                        <strong>PLAYER</strong>
                                        <small>End-user experience</small>
                                    </div>
                                </div>
                            </div>
                        </EthosinoTermsSection>

                        {/* 06 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-gaming"
                            number="06"
                            eyebrow="PARTICIPATION"
                            title="Games, competitions and participation"
                            theme="green"
                        >
                            <p>
                                The Services may provide access to casino-style games,
                                skill games, promotional games, tournaments,
                                sweepstakes-style experiences, peer-to-peer
                                competitions, poker, prediction products, or other
                                interactive entertainment depending on the applicable
                                operator and jurisdiction.
                            </p>

                            <p>
                                Each activity may be subject to its own rules,
                                eligibility requirements, entry mechanics, payout
                                structures, scoring systems, deadlines, and
                                restrictions.
                            </p>

                            <EthosinoTermsList>
                                <li>
                                    You are responsible for reviewing the applicable game
                                    or promotion rules before participating.
                                </li>

                                <li>
                                    A displayed potential prize or payout is not a
                                    guarantee of payment unless all applicable
                                    conditions are satisfied.
                                </li>

                                <li>
                                    Game outcomes may be determined by random,
                                    pseudo-random, provably-fair, skill-based,
                                    peer-to-peer, or third-party systems depending on the
                                    product.
                                </li>

                                <li>
                                    Technical errors, cancelled events, invalid entries,
                                    interrupted sessions, or other unusual circumstances
                                    may be resolved under applicable game rules.
                                </li>

                                <li>
                                    Final results may be subject to fraud, integrity,
                                    compliance, or technical review before settlement.
                                </li>
                            </EthosinoTermsList>

                            <div className="ethosino-terms-game-strip">
                                <span>CASINO</span>
                                <span>POKER</span>
                                <span>SKILL</span>
                                <span>TOURNAMENTS</span>
                                <span>THIRD-PARTY CONTENT</span>
                            </div>
                        </EthosinoTermsSection>

                        {/* 07 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-wallets"
                            number="07"
                            eyebrow="VALUE MOVEMENT"
                            title="Wallets, deposits, withdrawals and transactions"
                            theme="cyan"
                        >
                            <p>
                                Certain Ethosino-powered environments may include
                                virtual wallets, promotional balances, credits,
                                currency balances, sweepstakes-related balances, prize
                                balances, or other units of value.
                            </p>

                            <p>
                                The legal and economic characteristics of a balance
                                depend on the applicable service, operator, promotion,
                                and jurisdiction.
                            </p>

                            <div className="ethosino-terms-transaction-grid">
                                <div>
                                    <span>01</span>
                                    <strong>Deposits</strong>

                                    <p>
                                        Deposits may be subject to minimums, maximums,
                                        payment-provider rules, fraud screening, geographic
                                        restrictions, and verification.
                                    </p>
                                </div>

                                <div>
                                    <span>02</span>
                                    <strong>Withdrawals</strong>

                                    <p>
                                        Withdrawals may require identity verification,
                                        payment verification, compliance review, gameplay
                                        review, fraud review, and satisfaction of
                                        promotional conditions.
                                    </p>
                                </div>

                                <div>
                                    <span>03</span>
                                    <strong>Adjustments</strong>

                                    <p>
                                        Erroneous credits, duplicate payments, reversed
                                        transactions, technical errors, chargebacks, or
                                        invalid promotional credits may be corrected.
                                    </p>
                                </div>
                            </div>

                            <p>
                                A payment displayed as pending, initiated, approved, or
                                complete within an interface may still depend on a
                                third-party processor, financial institution,
                                blockchain network, operator approval, or settlement
                                process.
                            </p>

                            <p>
                                Ethosino does not guarantee that every payment method
                                will remain available or that every payment processor
                                will support every jurisdiction.
                            </p>
                        </EthosinoTermsSection>

                        {/* 08 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-bonuses"
                            number="08"
                            eyebrow="PROMOTIONAL SYSTEMS"
                            title="Bonuses, rewards and promotions"
                            theme="purple"
                        >
                            <p>
                                Operators may offer bonuses, free entries, credits,
                                loyalty benefits, promotional currency, tournaments,
                                referral programs, rewards, or similar incentives.
                            </p>

                            <p>
                                Promotions may have separate terms governing matters
                                such as:
                            </p>

                            <EthosinoTermsList>
                                <li>eligibility;</li>
                                <li>minimum or maximum deposits;</li>
                                <li>play-through requirements;</li>
                                <li>expiration;</li>
                                <li>game restrictions;</li>
                                <li>maximum conversion or redemption;</li>
                                <li>geographic restrictions;</li>
                                <li>one-per-user or one-per-household limitations;</li>
                                <li>abuse prevention;</li>
                                <li>withdrawal restrictions; and</li>
                                <li>disqualification conditions.</li>
                            </EthosinoTermsList>

                            <p>
                                Fraudulent, coordinated, automated, duplicate,
                                manipulated, or abusive participation in promotions may
                                result in cancellation of promotional benefits and
                                other account action.
                            </p>
                        </EthosinoTermsSection>

                        {/* 09 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-fairness"
                            number="09"
                            eyebrow="INTEGRITY LAYER"
                            title="Game integrity and provable fairness"
                            theme="green"
                        >
                            <p>
                                Certain Ethosino-developed games may provide
                                cryptographic or provably-fair verification
                                information such as server seed hashes, client seeds,
                                nonces, game identifiers, result records, or other
                                evidence relating to game outcomes.
                            </p>

                            <div className="ethosino-terms-fairness-panel">
                                <div>
                                    <span>SERVER SEED</span>
                                    <strong>Committed before result</strong>
                                </div>

                                <div>
                                    <span>CLIENT SEED</span>
                                    <strong>User / session entropy</strong>
                                </div>

                                <div>
                                    <span>NONCE</span>
                                    <strong>Sequential game state</strong>
                                </div>

                                <div>
                                    <span>RESULT</span>
                                    <strong>Verifiable output</strong>
                                </div>
                            </div>

                            <p>
                                Third-party games may use different fairness,
                                certification, randomness, or outcome mechanisms.
                                Ethosino does not represent that every third-party game
                                uses Ethosino's native provably-fair architecture.
                            </p>

                            <p>
                                Attempts to exploit vulnerabilities, manipulate
                                randomness, tamper with client software, alter game
                                requests, intercept or modify network traffic, or
                                interfere with result generation are prohibited.
                            </p>
                        </EthosinoTermsSection>

                        {/* 10 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-prohibited"
                            number="10"
                            eyebrow="PLATFORM INTEGRITY"
                            title="Prohibited conduct"
                            theme="red"
                        >
                            <p>
                                You may not misuse the Services or engage in conduct
                                that compromises users, operators, partners, the
                                platform, or the integrity of a game or transaction.
                            </p>

                            <div className="ethosino-terms-prohibited-grid">
                                <div>
                                    <span>01</span>
                                    <strong>Fraud</strong>
                                    <p>
                                        False identity, stolen payment methods, forged
                                        documents, chargeback abuse, or fraudulent
                                        transactions.
                                    </p>
                                </div>

                                <div>
                                    <span>02</span>
                                    <strong>Multi-accounting</strong>
                                    <p>
                                        Creating or controlling multiple accounts to evade
                                        restrictions or obtain benefits improperly.
                                    </p>
                                </div>

                                <div>
                                    <span>03</span>
                                    <strong>Automation</strong>
                                    <p>
                                        Unauthorized bots, scripts, automation, scraping,
                                        or automated gameplay.
                                    </p>
                                </div>

                                <div>
                                    <span>04</span>
                                    <strong>Collusion</strong>
                                    <p>
                                        Coordinating with other players to manipulate
                                        competitions, poker, promotions, or outcomes.
                                    </p>
                                </div>

                                <div>
                                    <span>05</span>
                                    <strong>System interference</strong>
                                    <p>
                                        Probing, exploiting, attacking, reverse engineering,
                                        disrupting, or circumventing platform controls.
                                    </p>
                                </div>

                                <div>
                                    <span>06</span>
                                    <strong>Location circumvention</strong>
                                    <p>
                                        Using spoofing, VPNs, proxies, or false information
                                        to defeat geographic restrictions where prohibited.
                                    </p>
                                </div>

                                <div>
                                    <span>07</span>
                                    <strong>Promotion abuse</strong>
                                    <p>
                                        Manipulating bonuses, referrals, credits, offers, or
                                        promotional qualification.
                                    </p>
                                </div>

                                <div>
                                    <span>08</span>
                                    <strong>Illegal use</strong>
                                    <p>
                                        Using the Services for money laundering, unlawful
                                        wagering, sanctions violations, or other illegal
                                        activity.
                                    </p>
                                </div>
                            </div>
                        </EthosinoTermsSection>

                        {/* 11 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-compliance"
                            number="11"
                            eyebrow="REGULATORY CONTROLS"
                            title="Identity, geolocation and compliance"
                            theme="cyan"
                        >
                            <p>
                                Ethosino or an applicable operator may use identity
                                verification, age verification, geolocation,
                                sanctions screening, fraud analysis, transaction
                                monitoring, responsible-gaming controls, or other
                                compliance procedures.
                            </p>

                            <p>
                                You may be required to provide documents or
                                information including:
                            </p>

                            <EthosinoTermsList>
                                <li>legal name;</li>
                                <li>date of birth;</li>
                                <li>government-issued identification;</li>
                                <li>address information;</li>
                                <li>proof of address;</li>
                                <li>payment ownership information;</li>
                                <li>source-of-funds information where required;</li>
                                <li>location permission;</li>
                                <li>tax information; or</li>
                                <li>
                                    additional documentation reasonably required for
                                    verification or compliance.
                                </li>
                            </EthosinoTermsList>

                            <p>
                                Access, transactions, withdrawals, or accounts may be
                                restricted while a compliance review is pending.
                            </p>
                        </EthosinoTermsSection>

                        {/* 12 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-third-party"
                            number="12"
                            eyebrow="CONNECTED INFRASTRUCTURE"
                            title="Third-party services and content"
                            theme="purple"
                        >
                            <p>
                                The Services may depend on or integrate with
                                third-party providers including:
                            </p>

                            <div className="ethosino-terms-provider-cloud">
                                <span>GAME STUDIOS</span>
                                <span>PAYMENTS</span>
                                <span>KYC / AML</span>
                                <span>GEOLOCATION</span>
                                <span>MESSAGING</span>
                                <span>ANALYTICS</span>
                                <span>CLOUD HOSTING</span>
                                <span>SPORTS DATA</span>
                                <span>WALLET SERVICES</span>
                            </div>

                            <p>
                                Third-party services may be subject to their own terms
                                and policies. Ethosino is not responsible for the
                                independent acts, omissions, availability, security,
                                policies, decisions, or performance of third-party
                                providers except to the extent required by law or an
                                applicable agreement.
                            </p>

                            <p>
                                Third-party content may be removed, restricted,
                                substituted, or become unavailable without notice due
                                to licensing, commercial, technical, regulatory, or
                                provider-related reasons.
                            </p>
                        </EthosinoTermsSection>

                        {/* 13 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-intellectual-property"
                            number="13"
                            eyebrow="OWNERSHIP"
                            title="Intellectual property"
                            theme="green"
                        >
                            <p>
                                Unless otherwise stated, Ethosino and its licensors
                                retain all right, title, and interest in Ethosino
                                software, source code, object code, designs,
                                interfaces, databases, documentation, graphics,
                                branding, platform architecture, proprietary games,
                                algorithms, APIs, trademarks, and related intellectual
                                property.
                            </p>

                            <p>
                                Subject to these Terms, users receive only a limited,
                                personal, revocable, non-exclusive,
                                non-transferable right to access the Services for their
                                intended purpose.
                            </p>

                            <p>
                                Unless expressly authorized, you may not:
                            </p>

                            <EthosinoTermsList>
                                <li>copy or reproduce Ethosino software;</li>
                                <li>sell or sublicense access;</li>
                                <li>reverse engineer proprietary components;</li>
                                <li>remove copyright or trademark notices;</li>
                                <li>extract databases or datasets;</li>
                                <li>create unauthorized derivative products;</li>
                                <li>impersonate Ethosino or an operator;</li>
                                <li>use Ethosino branding without permission; or</li>
                                <li>
                                    access non-public APIs or infrastructure without
                                    authorization.
                                </li>
                            </EthosinoTermsList>

                            <p>
                                Third-party game content, logos, software, artwork, and
                                trademarks remain the property of their respective
                                owners.
                            </p>
                        </EthosinoTermsSection>

                        {/* 14 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-availability"
                            number="14"
                            eyebrow="LIVE SOFTWARE"
                            title="Availability, maintenance and platform changes"
                            theme="cyan"
                        >
                            <p>
                                Ethosino is actively developed software. Features,
                                games, APIs, providers, interfaces, limits,
                                integrations, operator tools, or service availability
                                may change.
                            </p>

                            <p>
                                We may perform scheduled or emergency maintenance,
                                deploy updates, change infrastructure, modify features,
                                remove deprecated functionality, or temporarily
                                restrict access where reasonably necessary.
                            </p>

                            <div className="ethosino-terms-availability-panel">
                                <div>
                                    <span>UPDATES</span>
                                    <strong>Continuous</strong>
                                </div>

                                <div>
                                    <span>MAINTENANCE</span>
                                    <strong>As required</strong>
                                </div>

                                <div>
                                    <span>AVAILABILITY</span>
                                    <strong>Not guaranteed</strong>
                                </div>

                                <div>
                                    <span>SECURITY PATCHES</span>
                                    <strong>Priority</strong>
                                </div>
                            </div>

                            <p>
                                We do not guarantee uninterrupted or error-free access
                                to every component of the Services.
                            </p>
                        </EthosinoTermsSection>

                        {/* 15 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-disclaimers"
                            number="15"
                            eyebrow="SERVICE LIMITATIONS"
                            title="Disclaimers"
                            theme="purple"
                        >
                            <p>
                                To the fullest extent permitted by applicable law, the
                                Services are provided on an "as is" and "as available"
                                basis.
                            </p>

                            <p>
                                Ethosino does not warrant that:
                            </p>

                            <EthosinoTermsList>
                                <li>
                                    the Services will always be uninterrupted,
                                    error-free, or available;
                                </li>

                                <li>
                                    every feature will operate identically in every
                                    jurisdiction or operator environment;
                                </li>

                                <li>
                                    third-party services will remain available;
                                </li>

                                <li>
                                    any game, promotion, tournament, or feature will
                                    remain available indefinitely;
                                </li>

                                <li>
                                    information supplied by third parties will always be
                                    accurate or current;
                                </li>

                                <li>
                                    use of the Services will produce a particular
                                    commercial, gaming, financial, or entertainment
                                    result; or
                                </li>

                                <li>
                                    temporary technical interruptions will never occur.
                                </li>
                            </EthosinoTermsList>

                            <div className="ethosino-terms-warning-panel">
                                <EthosinoTermsIcon type="warning" />

                                <div>
                                    <span>NO GUARANTEED OUTCOME</span>

                                    <p>
                                        Gaming activity involves uncertainty. Ethosino does
                                        not guarantee winnings, profitability, returns,
                                        tournament outcomes, promotional rewards, or any
                                        particular result from participation.
                                    </p>
                                </div>
                            </div>
                        </EthosinoTermsSection>

                        {/* 16 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-liability"
                            number="16"
                            eyebrow="RISK ALLOCATION"
                            title="Limitation of liability"
                            theme="red"
                        >
                            <p>
                                To the maximum extent permitted by applicable law,
                                Ethosino and its affiliates, officers, employees,
                                contractors, licensors, and service providers will not
                                be liable for indirect, incidental, special,
                                consequential, exemplary, or punitive damages arising
                                from or relating to the Services.
                            </p>

                            <p>
                                This may include, where legally permitted, loss of
                                profits, loss of revenue, loss of data, loss of
                                business opportunity, interruption of business,
                                reputational harm, or similar damages.
                            </p>

                            <p>
                                Nothing in these Terms excludes or limits liability
                                that cannot legally be excluded or limited.
                            </p>

                            <p>
                                The allocation of responsibility between Ethosino and
                                an independent operator may be further governed by
                                agreements between Ethosino and that operator.
                            </p>
                        </EthosinoTermsSection>

                        {/* 17 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-indemnity"
                            number="17"
                            eyebrow="RESPONSIBILITY"
                            title="Indemnification"
                            theme="green"
                        >
                            <p>
                                To the extent permitted by applicable law, you agree to
                                defend, indemnify, and hold harmless Ethosino and its
                                affiliates from claims, liabilities, damages, losses,
                                costs, and expenses arising from or relating to:
                            </p>

                            <EthosinoTermsList>
                                <li>your unlawful use of the Services;</li>
                                <li>your violation of these Terms;</li>
                                <li>your violation of another person's rights;</li>
                                <li>fraudulent or abusive activity associated with you;</li>
                                <li>
                                    unauthorized access caused by your failure to protect
                                    account credentials; or
                                </li>
                                <li>
                                    information or content you submit in violation of
                                    applicable law.
                                </li>
                            </EthosinoTermsList>

                            <p>
                                This provision does not apply where prohibited by
                                applicable consumer-protection law.
                            </p>
                        </EthosinoTermsSection>

                        {/* 18 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-termination"
                            number="18"
                            eyebrow="ACCOUNT CONTROLS"
                            title="Suspension, restriction and termination"
                            theme="cyan"
                        >
                            <p>
                                Ethosino or an applicable operator may suspend,
                                restrict, investigate, or terminate access where
                                reasonably necessary to protect the platform, comply
                                with law, investigate suspicious activity, enforce
                                these Terms, or prevent harm.
                            </p>

                            <p>Examples include:</p>

                            <EthosinoTermsList>
                                <li>suspected fraud;</li>
                                <li>failed identity verification;</li>
                                <li>prohibited geographic access;</li>
                                <li>chargeback or payment abuse;</li>
                                <li>bonus abuse;</li>
                                <li>collusion;</li>
                                <li>account sharing;</li>
                                <li>security threats;</li>
                                <li>automated gameplay;</li>
                                <li>regulatory requirements;</li>
                                <li>self-exclusion restrictions; or</li>
                                <li>material violation of these Terms.</li>
                            </EthosinoTermsList>

                            <p>
                                Where appropriate, remaining balances or pending
                                transactions may be subject to applicable law,
                                verification, operator rules, chargeback rights, fraud
                                review, or other legitimate obligations following
                                suspension or termination.
                            </p>
                        </EthosinoTermsSection>

                        {/* 19 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-disputes"
                            number="19"
                            eyebrow="LEGAL FRAMEWORK"
                            title="Disputes and governing law"
                            theme="purple"
                        >
                            <div className="ethosino-terms-counsel-alert">
                                <span>ACTION REQUIRED BEFORE PUBLISHING</span>

                                <h3>
                                    Have legal counsel finalize this section.
                                </h3>

                                <p>
                                    Governing law, venue, arbitration, class-action
                                    waivers, mandatory informal dispute procedures, and
                                    other dispute provisions depend heavily on your
                                    corporate entity and operating jurisdictions.
                                </p>
                            </div>

                            <p>
                                Before initiating formal legal proceedings, you are
                                encouraged to contact Ethosino so that we may attempt to
                                resolve the dispute informally.
                            </p>

                            <p>
                                Notices concerning legal disputes may be directed to:
                            </p>

                            <div className="ethosino-terms-legal-address">
                                <span>LEGAL CONTACT</span>

                                <strong>legal@ethosino.com</strong>

                                <code>
                                    [ETHOSINO LEGAL ENTITY]
                                    <br />
                                    [REGISTERED ADDRESS]
                                    <br />
                                    [CITY, STATE / REGION]
                                    <br />
                                    [COUNTRY]
                                </code>
                            </div>

                            <p>
                                The governing law and forum applicable to these Terms
                                will be identified here after review by counsel.
                            </p>
                        </EthosinoTermsSection>

                        {/* 20 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-changes"
                            number="20"
                            eyebrow="VERSION CONTROL"
                            title="Changes to these Terms"
                            theme="green"
                        >
                            <p>
                                Ethosino may update these Terms to reflect changes in
                                the Services, technology, law, regulatory requirements,
                                business practices, security controls, or commercial
                                relationships.
                            </p>

                            <p>
                                When these Terms are updated, the "Last Updated" date
                                at the top of this page will be revised.
                            </p>

                            <p>
                                Material changes may be communicated through the
                                website, account interface, email, or another
                                reasonable notification method where appropriate or
                                legally required.
                            </p>

                            <p>
                                Continued use of the Services after updated Terms take
                                effect constitutes acceptance of the updated Terms to
                                the extent permitted by applicable law.
                            </p>
                        </EthosinoTermsSection>

                        {/* 21 */}
                        <EthosinoTermsSection
                            id="ethosino-terms-section-contact"
                            number="21"
                            eyebrow="CONTACT"
                            title="Contact Ethosino"
                            theme="cyan"
                        >
                            <p>
                                Questions concerning these Terms, the Services, or a
                                legal matter may be directed to:
                            </p>

                            <div className="ethosino-terms-contact-panel">
                                <div className="ethosino-terms-contact-brand">
                                    <span className="ethosino-terms-brand-mark">
                                        <span />
                                        <span />
                                    </span>

                                    <div>
                                        <strong>ETHOSINO</strong>
                                        <small>Legal & Platform Operations</small>
                                    </div>
                                </div>

                                <div className="ethosino-terms-contact-details">
                                    <div>
                                        <span>LEGAL</span>

                                        <a href="mailto:legal@ethosino.com">
                                            legal@ethosino.com
                                        </a>
                                    </div>

                                    <div>
                                        <span>SUPPORT</span>

                                        <a href="mailto:support@ethosino.com">
                                            support@ethosino.com
                                        </a>
                                    </div>

                                    <div>
                                        <span>PRIVACY</span>

                                        <a href="mailto:privacy@ethosino.com">
                                            privacy@ethosino.com
                                        </a>
                                    </div>

                                    <div>
                                        <span>WEBSITE</span>

                                        <a href="https://ethosino.com">
                                            ethosino.com
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="ethosino-terms-finalize-panel">
                                <span>BEFORE PRODUCTION</span>

                                <p>
                                    Replace the information below with the official
                                    contracting entity responsible for Ethosino.
                                </p>

                                <code>
                                    [FULL LEGAL ENTITY NAME]
                                    <br />
                                    [REGISTERED STREET ADDRESS]
                                    <br />
                                    [CITY, STATE / REGION, POSTAL CODE]
                                    <br />
                                    [COUNTRY]
                                </code>
                            </div>
                        </EthosinoTermsSection>

                        <footer
                            id="ethosino-terms-footer"
                            className="ethosino-terms-document-footer"
                        >
                            <div>
                                <span>ETHOSINO</span>
                                <strong>Terms & Conditions</strong>
                            </div>

                            <p>
                                Effective {ETHOSINO_TERMS_EFFECTIVE}
                                {" · "}
                                Last updated {ETHOSINO_TERMS_UPDATED}
                            </p>
                        </footer>
                    </article>
                </div>
            </main>
            <Footer />
        </Fragment>
    )
}